import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Lock, Phone, LogIn, UserPlus, X, Package, LogOut, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from './ui/sheet';

const AuthModal = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, login, register, logout, getOrders } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState('login'); // 'login', 'register', 'profile', 'forgot'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  
  // Form fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setName('');
    setPhone('');
    setError('');
    setSuccessMessage('');
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');
    
    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      if (response.ok) {
        setSuccessMessage('Si el email existe, recibirás un enlace de restablecimiento. / If the email exists, you will receive a reset link.');
      }
    } catch (err) {
      setError('Error al enviar el email. Intenta de nuevo.');
    }
    setLoading(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    const result = await login(email, password);
    
    if (result.success) {
      resetForm();
      setMode('profile');
      loadOrders();
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      setLoading(false);
      return;
    }
    
    const result = await register(email, password, name, phone);
    
    if (result.success) {
      resetForm();
      setMode('profile');
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const handleLogout = () => {
    logout();
    resetForm();
    setMode('login');
    setOrders([]);
  };

  const loadOrders = async () => {
    setLoadingOrders(true);
    const userOrders = await getOrders();
    setOrders(userOrders);
    setLoadingOrders(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      shipped: 'bg-purple-100 text-purple-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    const labels = {
      pending: 'Pendiente',
      processing: 'En Proceso',
      shipped: 'Enviado',
      completed: 'Completado',
      cancelled: 'Cancelado'
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100'}`}>
        {labels[status] || status}
      </span>
    );
  };

  // Render login form
  const renderLoginForm = () => (
    <form onSubmit={handleLogin} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            id="email"
            type="email"
            placeholder="tu@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="pl-10"
            required
          />
        </div>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="password">Contraseña</Label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            id="password"
            type="password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="pl-10"
            required
          />
        </div>
      </div>
      
      {error && (
        <p className="text-red-500 text-sm text-center">{error}</p>
      )}
      
      <Button 
        type="submit" 
        className="w-full bg-cyan-500 hover:bg-cyan-600"
        disabled={loading}
      >
        {loading ? 'Ingresando...' : 'Iniciar Sesión'}
      </Button>
      
      <button 
        type="button"
        onClick={() => { setMode('forgot'); setError(''); setSuccessMessage(''); }}
        className="w-full text-sm text-cyan-600 hover:underline"
      >
        ¿Olvidaste tu contraseña? / Forgot password?
      </button>
      
      <p className="text-center text-sm text-gray-500">
        ¿No tienes cuenta?{' '}
        <button 
          type="button"
          onClick={() => { setMode('register'); setError(''); }}
          className="text-cyan-600 hover:underline font-medium"
        >
          Regístrate
        </button>
      </p>
    </form>
  );

  // Render register form
  const renderRegisterForm = () => (
    <form onSubmit={handleRegister} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="name">Nombre Completo</Label>
        <div className="relative">
          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            id="name"
            type="text"
            placeholder="Juan Pérez"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="pl-10"
            required
          />
        </div>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            id="email"
            type="email"
            placeholder="tu@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="pl-10"
            required
          />
        </div>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="phone">Teléfono (opcional)</Label>
        <div className="relative">
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            id="phone"
            type="tel"
            placeholder="561-123-4567"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="password">Contraseña</Label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            id="password"
            type="password"
            placeholder="Mínimo 6 caracteres"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="pl-10"
            required
            minLength={6}
          />
        </div>
      </div>
      
      {error && (
        <p className="text-red-500 text-sm text-center">{error}</p>
      )}
      
      <Button 
        type="submit" 
        className="w-full bg-cyan-500 hover:bg-cyan-600"
        disabled={loading}
      >
        {loading ? 'Registrando...' : 'Crear Cuenta'}
      </Button>
      
      <p className="text-center text-sm text-gray-500">
        ¿Ya tienes cuenta?{' '}
        <button 
          type="button"
          onClick={() => { setMode('login'); setError(''); }}
          className="text-cyan-600 hover:underline font-medium"
        >
          Inicia Sesión
        </button>
      </p>
    </form>
  );

  // Render user profile
  const renderProfile = () => (
    <div className="space-y-6">
      {/* User Info */}
      <div className="bg-gradient-to-r from-cyan-50 to-blue-50 p-4 rounded-lg">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-cyan-500 rounded-full flex items-center justify-center">
            <User className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="font-semibold text-gray-900">{user?.name}</p>
            <p className="text-sm text-gray-500">{user?.email}</p>
          </div>
        </div>
      </div>
      
      {/* Go to Account Page Button */}
      <Button 
        onClick={() => {
          setIsOpen(false);
          navigate('/account');
        }}
        className="w-full bg-cyan-500 hover:bg-cyan-600"
        data-testid="go-to-account-page"
      >
        <Package className="w-4 h-4 mr-2" />
        Ver Mis Pedidos
        <ChevronRight className="w-4 h-4 ml-auto" />
      </Button>
      
      {/* Orders Section - Quick Preview */}
      <div>
        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Package className="w-5 h-5 text-cyan-600" />
          Pedidos Recientes
        </h3>
        
        {loadingOrders ? (
          <div className="text-center py-8 text-gray-500">Cargando...</div>
        ) : orders.length === 0 ? (
          <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
            <Package className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p>No tienes pedidos todavía</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-48 overflow-y-auto">
            {orders.slice(0, 3).map((order, idx) => (
              <div key={idx} className="border rounded-lg p-3 hover:bg-gray-50 transition-colors">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-medium text-sm">Pedido #{order.id?.slice(-8)}</p>
                    <p className="text-xs text-gray-500">{formatDate(order.created_at)}</p>
                  </div>
                  <div className="text-right">
                    {getStatusBadge(order.status)}
                    <p className="font-bold text-cyan-600 mt-1">${order.total?.toFixed(2)}</p>
                  </div>
                </div>
              </div>
            ))}
            {orders.length > 3 && (
              <button 
                onClick={() => {
                  setIsOpen(false);
                  navigate('/account');
                }}
                className="w-full text-center text-sm text-cyan-600 hover:underline py-2"
              >
                Ver todos los pedidos ({orders.length})
              </button>
            )}
          </div>
        )}
      </div>
      
      {/* Logout Button */}
      <Button 
        variant="outline" 
        onClick={handleLogout}
        className="w-full text-red-500 border-red-200 hover:bg-red-50"
      >
        <LogOut className="w-4 h-4 mr-2" />
        Cerrar Sesión
      </Button>
    </div>
  );

  // Render forgot password form
  const renderForgotForm = () => (
    <form onSubmit={handleForgotPassword} className="space-y-4">
      <p className="text-sm text-gray-600 text-center">
        Ingresa tu email y te enviaremos un enlace para restablecer tu contraseña.
        <br />
        <span className="text-gray-400 text-xs">
          Enter your email and we'll send you a reset link.
        </span>
      </p>
      
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            id="email"
            type="email"
            placeholder="tu@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="pl-10"
            required
          />
        </div>
      </div>
      
      {error && (
        <p className="text-red-500 text-sm text-center">{error}</p>
      )}
      
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 text-sm p-3 rounded text-center">
          {successMessage}
        </div>
      )}
      
      <Button 
        type="submit" 
        className="w-full bg-cyan-500 hover:bg-cyan-600"
        disabled={loading}
      >
        {loading ? 'Enviando...' : 'Enviar Enlace / Send Link'}
      </Button>
      
      <button 
        type="button"
        onClick={() => { setMode('login'); setError(''); setSuccessMessage(''); }}
        className="w-full text-sm text-gray-500 hover:text-cyan-600"
      >
        ← Volver al Login / Back to Login
      </button>
    </form>
  );

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <button 
          className="flex items-center gap-2 text-gray-600 hover:text-cyan-600 transition-colors"
          data-testid="auth-button"
        >
          <User className="w-5 h-5" />
          <span className="hidden md:inline text-sm">
            {isAuthenticated ? user?.name?.split(' ')[0] : 'Mi Cuenta'}
          </span>
        </button>
      </SheetTrigger>
      
      <SheetContent className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="text-cyan-600 flex items-center gap-2">
            {isAuthenticated ? (
              <>
                <User className="w-5 h-5" />
                Mi Cuenta
              </>
            ) : mode === 'login' ? (
              <>
                <LogIn className="w-5 h-5" />
                Iniciar Sesión
              </>
            ) : mode === 'forgot' ? (
              <>
                <Mail className="w-5 h-5" />
                Recuperar Contraseña
              </>
            ) : (
              <>
                <UserPlus className="w-5 h-5" />
                Crear Cuenta
              </>
            )}
          </SheetTitle>
        </SheetHeader>
        
        <div className="mt-6">
          {isAuthenticated ? renderProfile() : (
            mode === 'login' ? renderLoginForm() : 
            mode === 'forgot' ? renderForgotForm() :
            renderRegisterForm()
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default AuthModal;
